import { useSnackbar } from "notistack";
import React, { useContext, useState } from "react";

import { useDeleteImageQuery } from "api/services/useImageQuery";
import { CachePolicy } from "api/types";
import { JANZU_URL } from "config/config";
import { UNSURE_LABEL } from "config/LabelMapping";
import { BoundingBoxesContext, useBoundingBoxesContext } from "context/boundingBoxesContext";
import { useImageSettingsContext } from "context/ImageSettingsContext/imageSettingsContext";
import { DEFAULT_DISAGREEMENT_CHECK_RESPONSE, useReviewPage } from "context/reviewPageContext/reviewPageContext";
import { DisagreementNoteModal } from "features/disagreementsContent/components/DisagreementNoteModal";
import { ButtonPressedEnum } from "features/disagreementsContent/types";
import { useUser } from "hooks/user/useUser";
import { DetectorModes } from "types/createDetector";
import { ModeConfiguration } from "types/detectors";
import { DisagreementData } from "types/disagreements";
import { CanvasAnnotation } from "types/imageAnnotations";
import { DisplayBBoxInfo, Posicheck } from "types/imageQueries";
import { getCookie } from "utils/cookies";
import { stringClassNameMap } from "utils/disagreementsUtils";
import { generateImageUrl } from "utils/generateImageUrl";
import { createArrayRois } from "utils/ReviewPageUtils";

import { RelabelActionsView } from "./RelabelActionsView";

export interface RelabelSectionReviewPageProps {
  onClose: () => void;
  posicheckId?: string;
  lastLabel?: string | null;
  mode: DetectorModes;
  modeConfig?: ModeConfiguration;
  reviewBBoxInfo?: DisplayBBoxInfo;
}

export function RelabelSectionReviewPage(props: RelabelSectionReviewPageProps) {
  const { onClose, posicheckId, lastLabel, mode, modeConfig, reviewBBoxInfo } = props;

  const {
    currentImageQuery,
    imageQueryId,
    labelImageQuery,
    removeRecentIQ,
    startPooling,
    disagreementCheckResponse: { cameFromIQ, cameFromDisagreement, challengedAnswer },
    setDisagreementCheckResponse,
  } = useReviewPage();
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: deleteImageQuery, isLoading: isDeletingImageQuery } = useDeleteImageQuery();

  const contextValue = useContext(BoundingBoxesContext);
  const { isOnReview } = useImageSettingsContext();

  const [openDisagreementNoteDialog, setOpenDisagreementNoteDialog] = useState<boolean>(false);
  const [changedAnswer, setChangedAnswer] = useState<string | number | undefined>();
  const [feedbackText, setFeedbackText] = useState<string | undefined>();
  const [buttonPressed, setButtonPressed] = useState<ButtonPressedEnum | null>(null);

  const handleOpenDisagreementNoteDialog = (newLabel: string | number) => {
    setOpenDisagreementNoteDialog(true);
    setChangedAnswer(newLabel);
    const newAnswerMapped = stringClassNameMap[newLabel.toString()] ?? newLabel.toString();
    const previousAnswer = challengedAnswer;
    setButtonPressed(
      previousAnswer && previousAnswer === newAnswerMapped ? ButtonPressedEnum.KEEP : ButtonPressedEnum.CHANGE
    );
  };

  const handleUnclearOpenDisagreementNoteDialog = (feedbackText: string) => {
    handleOpenDisagreementNoteDialog(UNSURE_LABEL);
    setFeedbackText(feedbackText.toString());
  };

  const handleCloseDisagreementNoteDialog = () => {
    setOpenDisagreementNoteDialog(false);
    setChangedAnswer(undefined);
    setFeedbackText(undefined);
    setButtonPressed(null);
  };

  const { annotations } = useBoundingBoxesContext();

  const handleLabelImageQuery = async (
    currentImageQuery: Posicheck,
    label: string,
    feedbackText?: string,
    shouldRemoveRecentIQ: boolean = false
  ) => {
    let rois: CanvasAnnotation[] | undefined;
    if (contextValue && contextValue.isBoundingBoxes) {
      const { getValidBoxes } = contextValue;
      rois = createArrayRois(getValidBoxes());
    }
    onClose();
    return labelImageQuery(
      { currentImageQuery, label, annotations: rois, feedbackText, reviewBBoxInfo },
      shouldRemoveRecentIQ
    );
  };

  const shortCuitDisagreement = () => {
    setDisagreementCheckResponse(DEFAULT_DISAGREEMENT_CHECK_RESPONSE);
  };

  const handleSubmit = async (label: string | number, feedbackText?: string) => {
    if (!currentImageQuery) {
      return;
    }

    // Normal flow without disagreement checks if feature flag is off
    if (!user.should_create_disagreements) {
      await handleLabelImageQuery(
        currentImageQuery as Posicheck,
        label.toString(),
        feedbackText,
        true
      );

      return;
    }

    const posicheck = currentImageQuery as Posicheck;

    const answerLabel = label.toString();
    const response = await fetch(
      `${JANZU_URL}reef-api/disagreement-check?image_query_id=${posicheck.id}&answer=${answerLabel}`,
      {
        headers: {
          "Cache-Control": CachePolicy.NO_CACHE,
          Pragma: CachePolicy.NO_CACHE,
          "X-CSRFToken": getCookie("csrftoken") || "",
        },
      }
    );

    const disagreementData: DisagreementData = await response.json();

    if (disagreementData.is_disagreement) {
      setDisagreementCheckResponse({
        disagreementId: null,
        isDisagreement: true,
        challengedAnswer: stringClassNameMap[answerLabel],
        referenceAnswer: stringClassNameMap[disagreementData.reference_answer.class_name],
        cameFromIQ: true,
        cameFromDisagreement: false,
        isDisagreementUndo: false,
      });
    }

    try {
      const labelCreated = await handleLabelImageQuery(
        currentImageQuery as Posicheck,
        label.toString(),
        feedbackText,
        !disagreementData.is_disagreement
      );

      if (!labelCreated?.generate_disagreement) {
        shortCuitDisagreement();
      }
    } catch (error) {
      console.error(error);
      // short-cuit disagreement flow on error
      shortCuitDisagreement();
    }
  };

  const handleLabelSubmit = cameFromIQ || cameFromDisagreement ? handleOpenDisagreementNoteDialog : handleSubmit;

  const handleUnclear = async (feedbackText: string) => {
    await handleSubmit(UNSURE_LABEL, feedbackText);
  };

  const handleUnclearSubmit =
    cameFromIQ || cameFromDisagreement ? handleUnclearOpenDisagreementNoteDialog : handleUnclear;

  const handleDeleteImageQuery = async () => {
    if (!posicheckId) {
      return;
    }

    try {
      await deleteImageQuery(posicheckId);
    } catch (error) {
      // Fail loudly to the user and keep the IQ in the pool: nothing was deleted
      // server-side, so we must not run the success steps below.
      console.error(error);
      enqueueSnackbar("Failed to delete the image query. Please try again.", { variant: "error" });
      return;
    }

    // The review pool keys rows by the item's `id` (Disagreement.id for
    // disagreement items, which differs from iq_id), so remove by that key to
    // ensure disagreement rows are dropped after a successful delete.
    //
    // Pass addToHistory=false: the IQ is gone server-side, so it must not enter
    // the undo history where a lingering undo control could re-insert a
    // server-deleted IQ back into the local queue.
    removeRecentIQ(currentImageQuery?.id ?? posicheckId, false);
    onClose();
    startPooling();
    enqueueSnackbar("Image Query successfully deleted!.", { variant: "success" });
  };

  return (
    <>
      <RelabelActionsView
        containerId="relabel-section-review"
        isLoading={!posicheckId}
        onClose={onClose}
        posicheckId={posicheckId}
        lastLabel={lastLabel}
        mode={mode}
        modeConfig={modeConfig}
        onLabelSubmit={(label) => handleLabelSubmit(label)}
        onUnclearSubmit={handleUnclearSubmit}
        rois={annotations}
        showCloseButton={isOnReview}
        circularProgressSize={22}
        showDeleteButton
        onDeleteImageQuery={handleDeleteImageQuery}
        isDeletingImageQuery={isDeletingImageQuery}
      />
      {openDisagreementNoteDialog && (
        <DisagreementNoteModal
          imageUrl={generateImageUrl(imageQueryId || "disagreement")}
          iqId={imageQueryId || "disagreement"}
          buttonPressed={buttonPressed}
          openModal={openDisagreementNoteDialog}
          onClose={handleCloseDisagreementNoteDialog}
          changedAnswer={String(changedAnswer)}
          feedbackText={feedbackText}
        />
      )}
    </>
  );
}
